// Client-side mirror of ExportRequestLimits, so an oversized selection is
// refused in the builder rather than becoming a broken report tab.
//
// This is not belt-and-braces duplication — it guards a case the server cannot
// reach. The report URL carries the selection, and the browser URL is served by
// nginx, which never invokes application code:
//
//   route URL too long -> nginx 414 -> page never loads -> API never called
//                                   -> ExportRequestLimits never runs
//
// So the backend cap only ever protects the API request. The only place that can
// refuse before a too-long URL exists is here, where the URL is built.
//
// KEEP IN STEP BY HAND with ExportRequestLimits.MAX_PARAMS / MAX_LENGTH in
// backend/src/main/groovy/blabs/equivate/service/reporting/ExportRequestLimits.groovy.
// There is no automated check that the two agree, and drift is silent: the
// symptom is a 414 in a freshly opened report tab, which reads as the app being
// broken rather than as a selection being too large.
//
// A cross-language test was considered and dropped — neither dev container
// mounts both trees, and there is no frontend test runner to host it.

export const MAX_PARAMS = 300
export const MAX_LENGTH = 12288

// Below every ceiling in the chain: nginx and Netty both sit at 16,384, and the
// path plus parameter names take a few hundred characters on top of the values.
const MAX_URL_CHARS = 12000

/**
 * Checks a built query object against the same budget the API enforces, plus the
 * URL-length ceiling only the client can see.
 *
 * @param   {Object} query  the query params about to become a report URL
 * @returns {{ok: boolean, message?: string}}
 */
export function checkReportQuery(query) {
  const values = Object.values(query || {})
    .filter(v => v !== undefined && v !== null && v !== '')
    .flatMap(v => String(v).split(','))
    .map(v => v.trim())
    .filter(v => v.length > 0)

  if (values.length > MAX_PARAMS) {
    return {
      ok: false,
      message: `Too many values selected (${values.length}). Maximum is ${MAX_PARAMS} — ` +
               `select fewer fields or investments, or leave a filter empty to include all of it.`
    }
  }

  const totalLength = values.reduce((n, v) => n + v.length + 1, 0)
  if (totalLength > MAX_LENGTH) {
    return {
      ok: false,
      message: `Selected filters are too long (${totalLength} characters). Maximum is ${MAX_LENGTH}.`
    }
  }

  // Commas become %2C in the query string, so the wire length exceeds the raw
  // total by two characters per value.
  const encodedLength = new URLSearchParams(query || {}).toString().length
  if (encodedLength > MAX_URL_CHARS) {
    return {
      ok: false,
      message: `This selection makes the report link too long (${encodedLength} characters). ` +
               `Leave a filter empty to include all of it, which shortens the link.`
    }
  }

  return { ok: true }
}
